import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import ContentWithCards from "../components/shared/ContentWithCards";
import { FaBook, FaUsers } from "react-icons/fa";
import { getJoinedCommunitiesAction, getNotJoinedCommunitiesAction, leaveCommunityAction, searchJoinedCommunitiesAction } from "../redux/actions/communityActions";
import CommonLoading from "../components/loader/CommonLoading";
import JoinedCommunityCard from "../components/community/JoinedCommunityCard";
import CommunityCard from "../components/community/CommunityCard";
import { useToast } from "../contexts/ToastContext";

const MySkillsCommunities = () => {
  const dispatch = useDispatch();
  const { success: showSuccess, error: showError } = useToast();
  const [activeTab, setActiveTab] = useState("my-communities");
  const [loading, setLoading] = useState(true);
  const [leavingCommunity, setLeavingCommunity] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const joinedCommunities = useSelector((state) => state.community?.joinedCommunities);
  const totalJoinedCommunities = useSelector((state) => state.community?.totalJoinedCommunities || 0);
  const notJoinedCommunities = useSelector((state) => state.community?.notJoinedCommunities);

  // Update activeTab based on URL path
  useEffect(() => {
    const path = window.location.pathname;
    if (path.includes('/suggested')) {
      setActiveTab("suggested");
    } else {
      setActiveTab("my-communities");
    }
  }, []);

  // Debug logging
  useEffect(() => {
    console.log('Active Tab:', activeTab);
    console.log('Joined Communities:', joinedCommunities);
    console.log('Not Joined Communities:', notJoinedCommunities);
  }, [activeTab, joinedCommunities, notJoinedCommunities]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([
        dispatch(getJoinedCommunitiesAction()),
        dispatch(getNotJoinedCommunitiesAction())
      ]);
      setLoading(false);
    };
    fetchData();
  }, [dispatch]);

  const handleLeaveCommunity = async (communityName) => {
    if (leavingCommunity === communityName) return;
    
    setLeavingCommunity(communityName);
    try {
      await dispatch(leaveCommunityAction(communityName));
      showSuccess(`Successfully left ${communityName}!`);
      // Refresh communities after leaving
      await Promise.all([
        dispatch(getJoinedCommunitiesAction()),
        dispatch(getNotJoinedCommunitiesAction())
      ]);
    } catch (error) {
      showError(error.message || "Failed to leave community. Please try again.");
      console.error('Error leaving community:', error);
    } finally {
      setLeavingCommunity(null);
    }
  };

  // Rendered lists are handled inline below for clarity with search pagination

  // Debounced server-side search for joined communities
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const q = searchQuery.trim();
      if (q) {
        setIsSearching(true);
        dispatch(searchJoinedCommunitiesAction(q, 25, 0, { append: false })).finally(() => setIsSearching(false));
      } else {
        // restore default joined list
        dispatch(getJoinedCommunitiesAction());
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [dispatch, searchQuery]);

  if (loading) {
    return (
      <ContentWithCards>
        <div className="main-section bg-white border">
          <div className="p-3">
            <div className="flex items-center justify-start mb-4 border-b py-3 pl-2 pr-2">
              <div className="flex items-center gap-3">
                <span className="relative inline-flex w-8 h-8 items-center justify-center">
                  <FaBook className="w-7 h-7 text-primary-600" />
                  <FaUsers className="absolute -bottom-0.5 -right-0.5 w-4 h-4 text-primary-600 bg-white rounded-full" />
                </span>
                <h2 className="text-lg font-semibold text-gray-700">
                  My Skills Communities
                </h2>
              </div>
            </div>
            <div className="flex items-center justify-center py-12">
              <CommonLoading />
            </div>
          </div>
        </div>
      </ContentWithCards>
    );
  }

  // noCommunitiesMessage removed; inline messages are used below

  return (
    <ContentWithCards>
      <div className="main-section bg-white border">
        <div className="p-3">
          <div className="flex items-center justify-start mb-4 border-b py-3 pl-2 pr-2">
            <div className="flex items-center gap-3">
              <span className="relative inline-flex w-8 h-8 items-center justify-center">
                <FaBook className="w-7 h-7 text-primary-600" />
                <FaUsers className="absolute -bottom-0.5 -right-0.5 w-4 h-4 text-primary-600 bg-white rounded-full" />
              </span>
              <h2 className="text-lg font-semibold text-gray-700">
                My Skills Communities
              </h2>
            </div>
          </div>

          <div className="mb-6 w-full">
            <input
              type="text"
              placeholder="Search your communities..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full px-4 py-3 pl-3 pr-4 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent placeholder-gray-500 font-roboto-flex"
            />
          </div>

          {activeTab === "my-communities" && (
            joinedCommunities && joinedCommunities.length > 0 ? (
              <>
                <div className="grid grid-cols-1 gap-4">
                  {joinedCommunities.map((community) => (
                    <JoinedCommunityCard
                      key={community._id}
                      community={community}
                      onLeave={handleLeaveCommunity}
                      isLeaving={leavingCommunity === community.name}
                    />
                  ))}
                </div>
                {/* Load More when searching */}
                { (searchQuery.trim() && joinedCommunities.length < totalJoinedCommunities) && (
                  <div className="mt-4 flex justify-center">
                    <button
                      onClick={() => dispatch(searchJoinedCommunitiesAction(searchQuery.trim(), 25, joinedCommunities.length, { append: true }))}
                      className="px-4 py-2 text-sm font-medium rounded border border-gray-300 hover:bg-gray-50"
                      disabled={isSearching}
                    >
                      {isSearching ? 'Loading...' : 'Load More'}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <p className="text-lg text-gray-600 font-roboto-flex">
                  {searchQuery.trim() ? "No communities match your search." : "You haven't joined any skills communities yet."}
                </p>
                <p className="text-sm text-gray-500 mt-2 font-roboto-flex">Check back later for updates</p>
              </div>
            )
          )}

          {activeTab === "suggested" && notJoinedCommunities && notJoinedCommunities.length > 0 && (
            <div className="grid grid-cols-1 gap-4">
              {notJoinedCommunities.map((community) => (
                <CommunityCard key={community._id} community={community} />
              ))}
            </div>
          )}
        </div>
      </div>
    </ContentWithCards>
  );
};

export default MySkillsCommunities;

